import { useEffect, useState } from "react";
import { Loader2, Save, RotateCcw, X } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { fetchContent, updateContent, resetContent } from "../lib/api";
import { useContent } from "../context/ContentContext";
import { Field, TextArea, IconField, ImageField, ItemCard, AddButton, updateAt, removeAt } from "./editors";

export default function AdminContent() {
  const { refresh } = useContent();
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchContent().then(setDraft).catch(() => toast.error("Failed to load content")); }, []);

  const set = (key, val) => setDraft((p) => ({ ...p, [key]: val }));
  const setCompany = (patch) => setDraft((p) => ({ ...p, company: { ...p.company, ...patch } }));
  const setHero = (patch) => setDraft((p) => ({ ...p, hero: { ...p.hero, ...patch } }));

  const save = async () => {
    setSaving(true);
    try { await updateContent(draft); await refresh(); toast.success("Content saved & published"); }
    catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm("Reset all content to defaults? This cannot be undone.")) return;
    setSaving(true);
    try { const d = await resetContent(); setDraft(d); await refresh(); toast.success("Content reset to defaults"); }
    catch { toast.error("Reset failed"); }
    finally { setSaving(false); }
  };

  if (!draft) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>;

  return (
    <div data-testid="admin-content">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-semibold">Site Content</h1>
          <p className="mt-1 text-sm text-muted-foreground">Edit any section of the public website. Changes publish on save.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={reset} disabled={saving} className="inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-sm font-medium hover:bg-muted disabled:opacity-50" data-testid="content-reset-btn"><RotateCcw className="h-4 w-4" /> Reset</button>
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground transition-transform hover:scale-105 disabled:opacity-60" data-testid="content-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save & Publish
          </button>
        </div>
      </div>

      <Tabs defaultValue="company" className="mt-6">
        <TabsList className="flex h-auto flex-wrap justify-start gap-1">
          {["company", "hero", "stats", "services", "whyChoose", "industries", "values", "testimonials", "projects", "gallery"].map((t) => (
            <TabsTrigger key={t} value={t} data-testid={`content-tab-${t}`} className="capitalize">{t === "whyChoose" ? "Why Choose" : t}</TabsTrigger>
          ))}
        </TabsList>

        {/* COMPANY */}
        <TabsContent value="company" className="mt-5 space-y-4">
          <div className="grid gap-3 rounded-xl border border-border bg-card p-5 sm:grid-cols-2">
            <Field label="Company Name" value={draft.company.name} onChange={(v) => setCompany({ name: v })} />
            <Field label="Tagline" value={draft.company.tagline} onChange={(v) => setCompany({ tagline: v })} />
            <Field label="Phone" value={draft.company.phone} onChange={(v) => setCompany({ phone: v })} />
            <Field label="WhatsApp (digits only)" value={draft.company.whatsapp} onChange={(v) => setCompany({ whatsapp: v })} />
            <Field label="Email" value={draft.company.email} onChange={(v) => setCompany({ email: v })} />
            <Field label="Address" value={draft.company.address} onChange={(v) => setCompany({ address: v })} />
            <div className="sm:col-span-2"><Field label="Google Maps Embed URL" value={draft.company.mapEmbed} onChange={(v) => setCompany({ mapEmbed: v })} /></div>
          </div>

          <SectionBox title="Phone Numbers">
            <StringList label="Phones (shown on Contact & Footer)" items={draft.company.phones || []} onChange={(arr) => setCompany({ phones: arr })} />
          </SectionBox>

          <SectionBox title="Business Hours">
            {draft.company.hours.map((h, i) => (
              <ItemCard key={i} index={i} title={h.day || `Row ${i + 1}`} onRemove={() => setCompany({ hours: removeAt(draft.company.hours, i) })}>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label="Day(s)" value={h.day} onChange={(v) => setCompany({ hours: updateAt(draft.company.hours, i, { day: v }) })} />
                  <Field label="Time" value={h.time} onChange={(v) => setCompany({ hours: updateAt(draft.company.hours, i, { time: v }) })} />
                </div>
              </ItemCard>
            ))}
            <AddButton label="Add hours row" onClick={() => setCompany({ hours: [...draft.company.hours, { day: "", time: "" }] })} />
          </SectionBox>

          <SectionBox title="Social Links">
            {draft.company.socials.map((s, i) => (
              <ItemCard key={i} index={i} title={s.name || `Social ${i + 1}`} onRemove={() => setCompany({ socials: removeAt(draft.company.socials, i) })}>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label="Name" value={s.name} onChange={(v) => setCompany({ socials: updateAt(draft.company.socials, i, { name: v }) })} />
                  <Field label="URL" value={s.url} onChange={(v) => setCompany({ socials: updateAt(draft.company.socials, i, { url: v }) })} />
                </div>
              </ItemCard>
            ))}
            <AddButton label="Add social link" onClick={() => setCompany({ socials: [...draft.company.socials, { name: "", url: "#" }] })} />
          </SectionBox>
        </TabsContent>

        {/* HERO */}
        <TabsContent value="hero" className="mt-5">
          <div className="grid gap-4 rounded-xl border border-border bg-card p-5">
            <Field label="Badge text" value={draft.hero.badge} onChange={(v) => setHero({ badge: v })} />
            <TextArea label="Headline" value={draft.hero.title} onChange={(v) => setHero({ title: v })} rows={2} />
            <TextArea label="Subheadline" value={draft.hero.subtitle} onChange={(v) => setHero({ subtitle: v })} />
            <ImageField label="Hero Background Image URL" value={draft.hero.image} onChange={(v) => setHero({ image: v })} />
          </div>
        </TabsContent>

        {/* STATS */}
        <TabsContent value="stats" className="mt-5 space-y-3">
          {draft.stats.map((s, i) => (
            <ItemCard key={i} index={i} title={s.label || `Stat ${i + 1}`} onRemove={() => set("stats", removeAt(draft.stats, i))}>
              <div className="grid gap-3 sm:grid-cols-3">
                <Field label="Label" value={s.label} onChange={(v) => set("stats", updateAt(draft.stats, i, { label: v }))} />
                <Field label="Value" type="number" value={s.value} onChange={(v) => set("stats", updateAt(draft.stats, i, { value: v }))} />
                <Field label="Suffix" value={s.suffix} onChange={(v) => set("stats", updateAt(draft.stats, i, { suffix: v }))} />
              </div>
            </ItemCard>
          ))}
          <AddButton label="Add stat" onClick={() => set("stats", [...draft.stats, { label: "", value: 0, suffix: "+" }])} />
        </TabsContent>

        {/* SERVICES */}
        <TabsContent value="services" className="mt-5">
          <Accordion type="single" collapsible className="space-y-3">
            {draft.services.map((s, i) => (
              <AccordionItem key={i} value={`svc-${i}`} className="rounded-xl border border-border bg-card px-4">
                <AccordionTrigger className="font-heading">{s.title || `Service ${i + 1}`}</AccordionTrigger>
                <AccordionContent className="space-y-4 pb-5">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Field label="Title" value={s.title} onChange={(v) => set("services", updateAt(draft.services, i, { title: v }))} />
                    <Field label="Slug (URL)" value={s.slug} onChange={(v) => set("services", updateAt(draft.services, i, { slug: v }))} />
                    <IconField value={s.iconName} onChange={(v) => set("services", updateAt(draft.services, i, { iconName: v }))} />
                    <Field label="Short tagline" value={s.short} onChange={(v) => set("services", updateAt(draft.services, i, { short: v }))} />
                  </div>
                  <ImageField label="Service Image URL" value={s.image} onChange={(v) => set("services", updateAt(draft.services, i, { image: v }))} />
                  <TextArea label="Overview" value={s.overview} onChange={(v) => set("services", updateAt(draft.services, i, { overview: v }))} rows={4} />

                  <StringList label="Capabilities" items={s.capabilities} onChange={(arr) => set("services", updateAt(draft.services, i, { capabilities: arr }))} />

                  <NestedList title="Benefits" items={s.benefits} onChange={(arr) => set("services", updateAt(draft.services, i, { benefits: arr }))}
                    template={{ iconName: "Star", title: "", text: "" }}
                    render={(b, j, upd) => (<>
                      <IconField value={b.iconName} onChange={(v) => upd({ iconName: v })} />
                      <Field label="Title" value={b.title} onChange={(v) => upd({ title: v })} />
                      <TextArea label="Text" value={b.text} onChange={(v) => upd({ text: v })} rows={2} />
                    </>)} />

                  <NestedList title="Process Steps" items={s.process} onChange={(arr) => set("services", updateAt(draft.services, i, { process: arr }))}
                    template={{ title: "", text: "" }}
                    render={(p, j, upd) => (<>
                      <Field label="Step title" value={p.title} onChange={(v) => upd({ title: v })} />
                      <TextArea label="Step text" value={p.text} onChange={(v) => upd({ text: v })} rows={2} />
                    </>)} />

                  <NestedList title="FAQs" items={s.faqs} onChange={(arr) => set("services", updateAt(draft.services, i, { faqs: arr }))}
                    template={{ q: "", a: "" }}
                    render={(f, j, upd) => (<>
                      <Field label="Question" value={f.q} onChange={(v) => upd({ q: v })} />
                      <TextArea label="Answer" value={f.a} onChange={(v) => upd({ a: v })} rows={2} />
                    </>)} />

                  <button onClick={() => set("services", removeAt(draft.services, i))} className="text-sm font-medium text-destructive hover:underline">Delete this service</button>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="mt-3"><AddButton label="Add service" onClick={() => set("services", [...draft.services, { slug: `service-${draft.services.length + 1}`, iconName: "Star", title: "New Service", short: "", image: "", overview: "", capabilities: [], benefits: [], process: [], faqs: [] }])} /></div>
        </TabsContent>

        {/* WHY CHOOSE */}
        <TabsContent value="whyChoose" className="mt-5 space-y-3">
          {draft.whyChoose.map((w, i) => (
            <ItemCard key={i} index={i} title={w.title || `Item ${i + 1}`} onRemove={() => set("whyChoose", removeAt(draft.whyChoose, i))}>
              <div className="grid gap-3 sm:grid-cols-2">
                <IconField value={w.iconName} onChange={(v) => set("whyChoose", updateAt(draft.whyChoose, i, { iconName: v }))} />
                <Field label="Title" value={w.title} onChange={(v) => set("whyChoose", updateAt(draft.whyChoose, i, { title: v }))} />
              </div>
              <TextArea label="Text" value={w.text} onChange={(v) => set("whyChoose", updateAt(draft.whyChoose, i, { text: v }))} rows={2} />
            </ItemCard>
          ))}
          <AddButton label="Add reason" onClick={() => set("whyChoose", [...draft.whyChoose, { iconName: "Star", title: "", text: "" }])} />
        </TabsContent>

        {/* INDUSTRIES */}
        <TabsContent value="industries" className="mt-5">
          <SectionBox title="Industries Served">
            <StringList label="Industries" items={draft.industries} onChange={(arr) => set("industries", arr)} />
          </SectionBox>
        </TabsContent>

        {/* VALUES */}
        <TabsContent value="values" className="mt-5 space-y-3">
          {draft.values.map((v, i) => (
            <ItemCard key={i} index={i} title={v.title || `Value ${i + 1}`} onRemove={() => set("values", removeAt(draft.values, i))}>
              <Field label="Title" value={v.title} onChange={(val) => set("values", updateAt(draft.values, i, { title: val }))} />
              <TextArea label="Text" value={v.text} onChange={(val) => set("values", updateAt(draft.values, i, { text: val }))} rows={2} />
            </ItemCard>
          ))}
          <AddButton label="Add value" onClick={() => set("values", [...draft.values, { title: "", text: "" }])} />
        </TabsContent>

        {/* TESTIMONIALS */}
        <TabsContent value="testimonials" className="mt-5 space-y-3">
          {draft.testimonials.map((t, i) => (
            <ItemCard key={i} index={i} title={t.name || `Testimonial ${i + 1}`} onRemove={() => set("testimonials", removeAt(draft.testimonials, i))}>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Name" value={t.name} onChange={(v) => set("testimonials", updateAt(draft.testimonials, i, { name: v }))} />
                <Field label="Role / Company" value={t.role} onChange={(v) => set("testimonials", updateAt(draft.testimonials, i, { role: v }))} />
              </div>
              <TextArea label="Quote" value={t.text} onChange={(v) => set("testimonials", updateAt(draft.testimonials, i, { text: v }))} />
            </ItemCard>
          ))}
          <AddButton label="Add testimonial" onClick={() => set("testimonials", [...draft.testimonials, { name: "", role: "", text: "" }])} />
        </TabsContent>

        {/* PROJECTS */}
        <TabsContent value="projects" className="mt-5 space-y-3">
          {draft.projects.map((p, i) => (
            <ItemCard key={i} index={i} title={p.title || `Project ${i + 1}`} onRemove={() => set("projects", removeAt(draft.projects, i))}>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Title" value={p.title} onChange={(v) => set("projects", updateAt(draft.projects, i, { title: v }))} />
                <Field label="Category" value={p.category} onChange={(v) => set("projects", updateAt(draft.projects, i, { category: v }))} />
              </div>
              <ImageField label="Image URL" value={p.image} onChange={(v) => set("projects", updateAt(draft.projects, i, { image: v }))} />
              <TextArea label="Description" value={p.description} onChange={(v) => set("projects", updateAt(draft.projects, i, { description: v }))} rows={2} />
              <Field label="Outcome" value={p.outcome} onChange={(v) => set("projects", updateAt(draft.projects, i, { outcome: v }))} />
            </ItemCard>
          ))}
          <AddButton label="Add project" onClick={() => set("projects", [...draft.projects, { title: "", category: "Import & Export", image: "", description: "", outcome: "" }])} />
        </TabsContent>

        {/* GALLERY */}
        <TabsContent value="gallery" className="mt-5 space-y-3">
          {draft.gallery.map((g, i) => (
            <ItemCard key={i} index={i} title={g.alt || `Image ${i + 1}`} onRemove={() => set("gallery", removeAt(draft.gallery, i))}>
              <ImageField label="Image URL" value={g.src} onChange={(v) => set("gallery", updateAt(draft.gallery, i, { src: v }))} />
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Caption (alt)" value={g.alt} onChange={(v) => set("gallery", updateAt(draft.gallery, i, { alt: v }))} />
                <Field label="Category" value={g.category} onChange={(v) => set("gallery", updateAt(draft.gallery, i, { category: v }))} />
              </div>
            </ItemCard>
          ))}
          <AddButton label="Add image" onClick={() => set("gallery", [...draft.gallery, { src: "", alt: "", category: "Logistics" }])} />
        </TabsContent>
      </Tabs>

      <div className="mt-8 flex justify-end">
        <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-transform hover:scale-105 disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save & Publish
        </button>
      </div>
    </div>
  );
}

function SectionBox({ title, children }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <h3 className="mb-3 font-heading font-semibold">{title}</h3>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function StringList({ label, items, onChange }) {
  return (
    <div>
      <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</span>
      <div className="mt-2 flex flex-wrap gap-2">
        {items.map((it, i) => (
          <span key={i} className="inline-flex items-center gap-1 rounded-full border border-border bg-background py-1 pl-3 pr-1 text-sm">
            <input value={it} onChange={(e) => onChange(items.map((x, idx) => idx === i ? e.target.value : x))} className="w-32 bg-transparent text-sm focus:outline-none" />
            <button type="button" onClick={() => onChange(items.filter((_, idx) => idx !== i))} className="rounded-full p-1 text-muted-foreground hover:text-destructive"><X className="h-3.5 w-3.5" /></button>
          </span>
        ))}
        <button type="button" onClick={() => onChange([...items, "New"])} className="rounded-full border border-dashed border-primary/50 px-3 py-1 text-sm text-primary hover:bg-primary/5">+ Add</button>
      </div>
    </div>
  );
}

function NestedList({ title, items, onChange, template, render }) {
  return (
    <div className="rounded-lg border border-dashed border-border p-3">
      <p className="mb-2 text-sm font-semibold">{title}</p>
      <div className="space-y-3">
        {items.map((item, j) => (
          <ItemCard key={j} index={j} onRemove={() => onChange(removeAt(items, j))}>
            {render(item, j, (patch) => onChange(updateAt(items, j, patch)))}
          </ItemCard>
        ))}
        <AddButton label={`Add ${title.toLowerCase()}`} onClick={() => onChange([...items, { ...template }])} />
      </div>
    </div>
  );
}
